import React, { useEffect, useRef } from 'react'
import { PRE_CHECK_MACHINE_STATES } from '../constants'
import useStudentPreChecks from '../useStudentPreChecks'
import { COPY_STRINGS } from '../../../constants/translatedCopy'
import { amiraGestures } from '../../Coach/GesturedSpeech'
import { delay } from '../../../services/util'
const renegadeSong = require('../../../../assets/sounds/dances/Renegadev3.mp3');
import NetworkPingProblemInstruction from './Components/NetworkPingProblemInstruction'
import networkPingTestController from './functions/networkPingTestController'
import useWindowEvents from '../useWindowEvents'
import { GetBrandedTutorName } from '../../../services/BrandingService'
import { getContentByGradeBand, getCopyByGradeband } from '../../../services/ThemingService'

const NETWORK_PING_EVENTS = {
    NETWORK_PING_FAILED: "NETWORK_PING_FAILED",
    MANUAL_RETRY: "MANUAL_RETRY",
    FORCED_SKIP_NETWORK_CHECK: "FORCED_SKIP_NETWORK_CHECK"
}

export default function NetworkPingChecks(props){

    const { speechProps, toggleReaderContainer } = props

    /**
     * Setup the studentPreCheck hook object to subscribe to the isCurrent event and use it to interact with the flow.
     */
    const studentPreCheck = useStudentPreChecks({itemToCheck: PRE_CHECK_MACHINE_STATES.networkPingChecks})
    const { isCurrent, currentError, settings } = studentPreCheck

    useWindowEvents()

    /**
     * Listens to the isCurrent state, when this check becomes current, start the check.
     */
    useEffect(()=>{
        if(isCurrent){
            studentPreCheck.startCheck()
            startNetworkPingChecks()
        }
    }, [isCurrent])

    /**
     * Mutable values
     */
     const networkPingTestControl = useRef(networkPingTestController())

    /**
     * Starts and controls which experience the user should go through
     */
    const startNetworkPingChecks = async () => {
        try{
            if(settings.shouldGoThroughFTUE){
                if(settings.shouldSkipFTUEIntro){
                    await performHardwareChecksAfterMicrophonePermissionFix()
                }else{
                    await networkPingTestControl.current.runTest()
                    await performNetworkFTUESpeech()
                }
            }else{
                await networkPingTestControl.current.runTest()
                await performNetworkWithoutFTUEIntros()
            }

            studentPreCheck.finishCheck()

        } catch(err) {
            try{
                speechProps.abortDance();
                speechProps.abortSpeech();
            }catch(err){
                console.log("Error aborting dance and speech.", err)
            }
            console.log("Network ping check failed", err)
            studentPreCheck.logEvent("NETWORK_CHECK_FAILED")
            studentPreCheck.failedCheck(new Error(err.message,{
                cause: {networkError: err}
            }))
        }
    }

    /**
     * The full FTUE experience for first time users
     */
    const performNetworkFTUESpeech = async () => {
        const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks');

        speechProps.playDance({
            gesture: amiraGestures.Eyebrows_excitement,
        });
        const excitedGesture = getContentByGradeBand({
            medium: amiraGestures.Wave,
            high: amiraGestures.Wave,
            default: amiraGestures.Excited_nomouth
        });
        speechProps.playDance({
            gesture: excitedGesture,
        });
        await speechProps.speakWithPromise({textToSpeak: studentChecks.intro})
        speechProps.abortDance();
        await speechProps.speakWithPromise({textToSpeak: studentChecks.intro_excited})
        const shouldPlayDance = getContentByGradeBand({
            medium: false,
            high: false,
            default: true
        });
        if (shouldPlayDance) {
            speechProps.playDance({
                gesture: amiraGestures.TheRenegadeDance_short,
            });
            await speechProps.playAppSoundsWithPromise({
                url: renegadeSong,
                key: 'renegadeSong',
            }, 7500);
        }

        const shouldPlayExasperatedToWave = getContentByGradeBand({
            medium: false,
            high: false,
            default: true
        });
        if (shouldPlayExasperatedToWave) {
            speechProps.playDance({
                gesture: amiraGestures.Exasperated_to_Wave,
            });
        }
        await speechProps.speakWithPromise({textToSpeak: studentChecks.intro_continued});
        await speechProps.speakWithPromise({textToSpeak: studentChecks.intro_finished(GetBrandedTutorName())});

        speechProps.abortDance();
    }

    /**
     * Shortened version for users has already seen the FTUE experience.
     */
    const performNetworkWithoutFTUEIntros = async () => {
        speechProps.playDance({
            gesture: amiraGestures.Wave,
        });
        const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks');
        await speechProps.speakWithPromise({textToSpeak: studentChecks.welcome_back});
        await speechProps.speakWithPromise({textToSpeak: studentChecks.wait_while_i_check});
    }

    /**
     * After mic permission fix, we don't want to play the intros again.
     */
    const performHardwareChecksAfterMicrophonePermissionFix = async () => {
        studentPreCheck.finishCheck() 
    }

    /**
     * The retry logic if it failed.
     */
    const retry = () => {
        studentPreCheck.logEvent(NETWORK_PING_EVENTS.MANUAL_RETRY)
        studentPreCheck.retryWith(startNetworkPingChecks)
    }

    const forceThroughNetworkCheck = () => {
        speechProps.abortSpeech()
        studentPreCheck.logEvent(NETWORK_PING_EVENTS.FORCED_SKIP_NETWORK_CHECK)
        studentPreCheck.failedCheckAndMoveOn()
    }

    /**
     * When this check is not current, it won't render anything and does nothing until it becomes current.
     */
    if(!isCurrent) return null

    /**
     * Error UI for when the check has errors.
     * Optionally against each error and handle them differently by doing currentError === "your_error"
     */
    if(currentError){
        return <NetworkPingProblemInstruction 
            speechProps={speechProps} 
            retry={retry}
            forceThroughNetworkCheck={forceThroughNetworkCheck} 
            toggleReaderContainer={toggleReaderContainer}/>
    }

    /**
     * Default UI for when the check is current.
     */
    return <div className='invisible'>Loading network ping checks</div>
}